import * as React from "react"
import {
  Button,
  Link,
  ThemeCss,
  DropdownMenu,
  DropdownMenuPopover,
  DropdownMenuItemsLowLevel,
  DropdownMenuItem,
  DropdownMenuButton,
} from "gatsby-interface"
import { MdSubdirectoryArrowLeft, MdExpandMore } from "react-icons/md"
import {
  HttpsOnBadge,
  GenerationFailed,
  GeneratingCertBadge,
  DomainErrorMessage,
  WarningIcon,
} from "./DomainDetails.elements"
import {
  HostingDomain,
  CertificateStatus,
  HostingCertificate,
} from "@modules/graphql/types"
import {
  useRedirectDefaultToPrimaryMutation,
  HostingDetailsForSiteDocument,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import { useJustCreatedCertificate } from "../hooks/useJustCreatedCertificate"
import { Bull } from "@modules/ui/components/Bull"
import { useFlags } from "@modules/featureFlags"
import { positionRight } from "@reach/popover"

// the value in seconds wich differentiates the old certs from the ones just created.
// for the last ones we render a special message that the cert is being uploaded to cdn
export const CERT_CREATION_THRESHOLD_IN_SECONDS = 90

const rowCss: ThemeCss = theme => ({
  minHeight: theme.space[8],
  marginBottom: theme.space[3],
  display: `grid`,

  "&:last-child": {
    marginBottom: 0,
  },

  [theme.mediaQueries.desktop]: {
    alignItems: `center`,
    display: `flex`,
    flexWrap: `wrap`,
  },
})

const secondaryDomainIconCss: ThemeCss = theme => ({
  transform: `translate(-10%, 10%) rotate(90deg) scale(1.1)`,
  color: theme.colors.purple[60],
  marginRight: theme.space[2],
})

const domainCss: ThemeCss = theme => ({
  fontWeight: theme.fontWeights.semiBold,
  lineHeight: theme.lineHeights.solid,
  color: theme.colors.grey[50],
  marginRight: theme.space[4],
  fontSize: theme.fontSizes[1],

  "&:hover": {
    color: theme.colors.purple[60],
  },
})

const verifiedDomainCss: ThemeCss = theme => ({
  color: theme.colors.grey[80],
})

const domainWithErrorCss: ThemeCss = theme => ({
  color: theme.colors.red[80],

  "&:hover": {
    color: `inherit`,
  },
})

const actionsCss: ThemeCss = theme => ({
  width: `100%`,
  display: `flex`,
  justifyContent: `flex-end`,

  [theme.mediaQueries.desktop]: {
    marginLeft: `auto`,
    width: `auto`,
  },
})

const validationCss: ThemeCss = theme => ({
  alignItems: `center`,
  display: `flex`,

  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
})

const domainLevelCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
})

export type DomainDetailsProps = {
  domain: HostingDomain
  siteId: string
  onRemove: () => void
  onRequestCertificate: () => void
  primaryDomainOptions?: HostingDomain[]
  certificate?: HostingCertificate
  threshold?: number
  hasSecondary?: boolean
  validating?: boolean
  onMakePrimary?: () => void
  allowToEdit?: boolean
}

export function DomainDetails({
  domain,
  onRemove,
  onMakePrimary,
  onRequestCertificate,
  certificate,
  siteId,
  primaryDomainOptions,
  threshold = CERT_CREATION_THRESHOLD_IN_SECONDS,
  hasSecondary,
  validating,
  allowToEdit,
}: DomainDetailsProps) {
  const [
    redirectDefaultToPrimaryMutation,
  ] = useRedirectDefaultToPrimaryMutation()
  const { flags } = useFlags()

  const {
    id,
    verified,
    errorMessage,
    cloudCdnDefault,
    certificateStatus,
    primaryDomainId,
  } = domain

  const handleOnRedirectPrimary = async (domainName?: string) => {
    redirectDefaultToPrimaryMutation({
      variables: {
        siteId,
        domainName,
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
      ],
    })
  }

  const { isJustCreatedCert } = useJustCreatedCertificate({
    certificate,
    certificateStatus,
    threshold,
  })

  return (
    <React.Fragment>
      <div
        css={theme => [
          rowCss(theme),
          primaryDomainId &&
            !cloudCdnDefault && {
              marginTop: `-${theme.space[4]}`,
            },
        ]}
      >
        <div>
          {primaryDomainId && !cloudCdnDefault && (
            <MdSubdirectoryArrowLeft css={secondaryDomainIconCss} />
          )}

          {!errorMessage ? (
            <Link
              variant="SIMPLE"
              href={`https://${id}`}
              css={theme => [
                domainCss(theme),
                verified && verifiedDomainCss(theme),
              ]}
            >
              {id}
            </Link>
          ) : (
            <span css={theme => [domainCss(theme), domainWithErrorCss(theme)]}>
              {id}
            </span>
          )}
        </div>

        <div css={{ display: `flex`, alignItems: `center` }}>
          {cloudCdnDefault && <HttpsOnBadge />}

          {certificateStatus === CertificateStatus.Issued &&
            !isJustCreatedCert && <HttpsOnBadge />}

          {certificateStatus === CertificateStatus.Failed && verified && (
            <GenerationFailed />
          )}

          {certificateStatus === CertificateStatus.Issued &&
            isJustCreatedCert && (
              <GeneratingCertBadge
                message={text.messages.uploadingCertificate}
              />
            )}

          {!errorMessage &&
            certificateStatus === CertificateStatus.Requested && (
              <GeneratingCertBadge
                message={text.messages.generatingCertificate}
              />
            )}

          {!verified && !cloudCdnDefault && !errorMessage && (
            <span css={validationCss}>
              <WarningIcon />
              {validating
                ? text.messages.validating
                : text.messages.notValidated}
            </span>
          )}

          {primaryDomainId && cloudCdnDefault && (
            <span css={domainLevelCss}>
              <Bull />
              {primaryDomainId
                ? `Redirects to ${primaryDomainId}`
                : `Primary domain`}
            </span>
          )}

          {(primaryDomainId || hasSecondary) && !cloudCdnDefault && (
            <span css={domainLevelCss}>
              <Bull />
              {primaryDomainId ? `Redirects to primary` : `Primary domain`}
            </span>
          )}
        </div>

        <div css={actionsCss}>
          {cloudCdnDefault &&
            primaryDomainOptions &&
            primaryDomainOptions.length > 0 && (
              <DropdownMenu>
                <Button
                  ButtonComponent={DropdownMenuButton}
                  data-cy={`edit-deploy-target-button`}
                  size="S"
                  variant="GHOST"
                  tone="BRAND"
                  rightIcon={<MdExpandMore />}
                >
                  Select Primary Redirect
                </Button>
                <DropdownMenuPopover position={positionRight}>
                  <DropdownMenuItemsLowLevel size="MAX_CONTENT">
                    <DropdownMenuItem
                      key={domain.id}
                      onSelect={handleOnRedirectPrimary}
                    >
                      No redirect
                    </DropdownMenuItem>
                    {primaryDomainOptions.map(domain => {
                      return (
                        <DropdownMenuItem
                          key={domain.id}
                          onSelect={() => handleOnRedirectPrimary(domain.id)}
                        >
                          {domain.id === primaryDomainId
                            ? `Redirecting to: ${domain.id}`
                            : `Redirect to: ${domain.id}`}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuItemsLowLevel>
                </DropdownMenuPopover>
              </DropdownMenu>
            )}
        </div>

        {!cloudCdnDefault && (
          <div css={actionsCss}>
            {allowToEdit && primaryDomainId && !cloudCdnDefault && (
              <Button variant="GHOST" size="S" onClick={onMakePrimary}>
                {text.actions.makePrimary}
              </Button>
            )}

            {allowToEdit && certificateStatus === CertificateStatus.Deleted && (
              <Button variant="GHOST" size="S" onClick={onRequestCertificate}>
                {text.actions.requestCertificate}
              </Button>
            )}

            {allowToEdit &&
              certificateStatus === CertificateStatus.Failed &&
              verified && (
                <Button variant="GHOST" size="S" onClick={onRequestCertificate}>
                  {text.actions.requestCertificateAgain}
                </Button>
              )}

            {allowToEdit && !cloudCdnDefault && (
              <Button variant="GHOST" size="S" onClick={onRemove}>
                {text.actions.remove}
              </Button>
            )}
          </div>
        )}
      </div>

      {domain && errorMessage && <DomainErrorMessage message={errorMessage} />}
    </React.Fragment>
  )
}
